// Pattern from https://tkdodo.eu/blog/effective-react-query-keys

import { IMetricsType } from "components/Charts/SingleMetricChart";

export const customObjectQueryKeys = {
  all: ["custom_objects"] as const,
  count: (objectName: string, segmentId?: number) =>
    [...customObjectQueryKeys.all, objectName, "count", segmentId] as const,
  allLists: (objectName: string) =>
    [...customObjectQueryKeys.all, objectName, "list"] as const,
  list: (objectName: string, filters: any = {}) =>
    [...customObjectQueryKeys.allLists(objectName), filters] as const,
  segment: (objectName: string, segmentId: number, filters: any = {}) =>
    [
      ...customObjectQueryKeys.allLists(objectName),
      segmentId,
      filters,
    ] as const,
  details: (objectName: string) =>
    [...customObjectQueryKeys.all, objectName, "detail"] as const,
  detail: (objectName: string, id: number) =>
    [...customObjectQueryKeys.details(objectName), id] as const,
};

export const customObjectAssociationsQueryKeys = {
  all: ["custom_objects_associations"] as const,
  allLists: () => [...customObjectAssociationsQueryKeys.all, "list"] as const,
  list: (associationId: number, objectId: number | string, filters: any = {}) =>
    [
      customObjectAssociationsQueryKeys.all,
      "list",
      associationId,
      objectId,
      filters,
    ] as const,
};

export const syncQueryKeys = {
  all: ["sync"] as const,
  allForService: (serviceId: string) =>
    [...syncQueryKeys.all, serviceId] as const,
  enable: (serviceId: string) =>
    [...syncQueryKeys.all, serviceId, "enable"] as const,
  health: (serviceId: string) =>
    [...syncQueryKeys.all, serviceId, "health"] as const,
};

export const salesforceSyncQueryKeys = {
  all: ["salesforce-sync"] as const,
  settings: () => [...salesforceSyncQueryKeys.all, "settings"] as const,
  connectInfo: () => [...salesforceSyncQueryKeys.all, "connect-info"] as const,
  /** The Start card of a Sandbox: what Salesforce holds that could become people */
  startEstimate: () =>
    [...salesforceSyncQueryKeys.all, "start-estimate"] as const,
  health: () => [...salesforceSyncQueryKeys.all, "health"] as const,
  /** The Sync criteria as the server holds them; `previous` also asks for the rules before the last change */
  criteria: (previous = false) =>
    [
      ...salesforceSyncQueryKeys.all,
      "criteria",
      previous ? "previous" : "current",
    ] as const,
  /**
   * Whether Start syncing has been pressed, read from the stored hold (`started`
   * on the criteria route). Its own entry: the Sync criteria screen's read is
   * never cached, and a page-level observer must not keep an old answer alive.
   */
  setupStarted: () =>
    [...salesforceSyncQueryKeys.all, "setup-started"] as const,
  /** The count of a rule change, by its id (the review that opens on Save) */
  criteriaCount: (countId: string | null) =>
    [
      ...salesforceSyncQueryKeys.all,
      "criteria-count",
      countId ?? "none",
    ] as const,
  /** The Campaigns of the customer's Salesforce, for the Campaign picker (CMP1) */
  campaignPicker: () =>
    [...salesforceSyncQueryKeys.all, "campaign-picker"] as const,
  /** What the saved Campaign choice costs, for the note on the Campaigns card (CMP1); `key` is the fingerprint of the saved rules */
  campaignCardEstimate: (key: string) =>
    [...salesforceSyncQueryKeys.all, "campaign-card-estimate", key] as const,
  /** The member counts and the last removal check of the Campaigns a page shows (CMP2); `ids` is the sorted list of their Salesforce ids */
  campaignPageInfo: (ids: string[]) =>
    [...salesforceSyncQueryKeys.all, "campaign-page-info", ids] as const,
  adminShadow: (accountId: number) =>
    [...salesforceSyncQueryKeys.all, "admin", accountId, "shadow"] as const,
  adminV2Flows: (accountId: number) =>
    [...salesforceSyncQueryKeys.all, "admin", accountId, "v2-flows"] as const,
};

export const salesforceTriggerQueryKeys = {
  all: ["salesforce-triggers"] as const,
  stages: () => [...salesforceTriggerQueryKeys.all, "stages"] as const,
  campaigns: () => [...salesforceTriggerQueryKeys.all, "campaigns"] as const,
  statuses: (campaignId: string) =>
    [...salesforceTriggerQueryKeys.all, "statuses", campaignId] as const,
  estimate: (body: unknown) =>
    [...salesforceTriggerQueryKeys.all, "estimate", body] as const,
  /** Where one Opportunity trigger stands; `stepId` is the flow step, left out for a trigger not saved yet */
  opportunityStatus: (stepId: number | undefined) =>
    [
      ...salesforceTriggerQueryKeys.all,
      "opportunity-status",
      stepId ?? "none",
    ] as const,
  /** The Opportunity fields a trigger can start on or filter by */
  opportunityFields: () =>
    [...salesforceTriggerQueryKeys.all, "opportunity-fields"] as const,
};

export const salesforceStepQueryKeys = {
  all: ["salesforce-steps"] as const,
  objects: () => [...salesforceStepQueryKeys.all, "objects"] as const,
  fields: (objectType: string) =>
    [...salesforceStepQueryKeys.all, "fields", objectType] as const,
  campaigns: () => [...salesforceStepQueryKeys.all, "campaigns"] as const,
  statuses: (campaignId: string) =>
    [...salesforceStepQueryKeys.all, "statuses", campaignId] as const,
  owners: () => [...salesforceStepQueryKeys.all, "owners"] as const,
};

export const formQueryKeys = {
  all: ["form"] as const,
  detail: (id: string) => [...formQueryKeys.all, id] as const,
  formWithAccount: (id: string) => ["formWithAccount", id] as const,
  share: (id: string) => ["share", id] as const,
  accountInfo: (id: string) => ["formAccountInfo", id] as const,
  formSubmissions: (id: string) => ["formSubmissions", id] as const,
  isFormChanged: (id: string) => ["isFormChanged", id] as const,
};

export const eventManagementKeys = {
  all: ["event_management"] as const,
  allWithCount: ["event_management_count"] as const,
  detail: (id?: number) => [...eventManagementKeys.all, id] as const,
  allMetrics: ["event_management_metric"] as const,
  endUserEvents: (id: number) =>
    ["event_management_end_user_event", id] as const,
};

export const hubSpotListsQueryKey = (hubSpotObjectType: string) =>
  ["hubspot", "lists", hubSpotObjectType] as const;

export const stepPeopleQueryKeys = {
  all: ["step-people"] as const,
  step: (stepId: number, type: string) =>
    [...stepPeopleQueryKeys.all, stepId, type] as const,
};

export const metricsEventsKeys = {
  events: ({
    emailIds,
    type,
    endDate,
    startDate,
    flowIds,
    broadcastId,
    abTestVariantId,
    stepId,
  }: {
    emailIds?: IEmailContent["id"][];
    type: IMetricsType;
    endDate?: Date;
    startDate?: Date;
    flowIds?: IIntegration["id"][];
    broadcastId?: number;
    abTestVariantId?: string;
    stepId?: number;
  }) =>
    [
      "metricsEvents",
      emailIds,
      type,
      endDate,
      startDate,
      flowIds,
      broadcastId,
      abTestVariantId,
      stepId,
    ] as const,
};
